import {
  type CreateLockerverseAuctionOptions,
  createLockerverseAuctionClient,
  type LockerverseAuction,
  type LockerverseAuctionClient,
  type LockerverseSdkError,
} from "@lockerverse/sdk/auctions";
import { useCallback, useEffect, useRef, useState } from "react";
import { useImmutableResourceIdentity } from "./immutable-resource-identity.ts";
import {
  type AuctionRefreshOptions,
  useAuctionReturnRefresh,
} from "./use-auction-return-refresh.ts";

export type UseLockerverseAuctionOptions = AuctionRefreshOptions & {
  /** Initial server snapshot. The hook still refreshes on mount. */
  initialAuction?: LockerverseAuction;
  /** Include one unlisted item when refreshing its direct item page. */
  previewItemSlug?: string;
};

export type UseLockerverseAuctionResult = {
  client: LockerverseAuctionClient;
  /** Last successful snapshot, retained during refresh and refresh errors. */
  auction: LockerverseAuction | null;
  loading: boolean;
  error: LockerverseSdkError | null;
  refresh: () => void;
};

/** Load one auction by slug without requesting the community auction list. */
export function useLockerverseAuction(
  options: CreateLockerverseAuctionOptions,
  auctionSlug: string,
  refreshOptions: UseLockerverseAuctionOptions = {}
): UseLockerverseAuctionResult {
  useImmutableResourceIdentity("auction", {
    apiBaseUrl: options.apiBaseUrl,
    communitySlug: options.communitySlug,
    environment: options.environment,
    mediaBaseUrl: options.mediaBaseUrl,
    previewItemSlug: refreshOptions.previewItemSlug,
    slug: auctionSlug,
  });
  const onErrorRef = useRef(options.onError);
  onErrorRef.current = options.onError;
  const [client] = useState(() =>
    createLockerverseAuctionClient({
      ...options,
      onError: (error) => onErrorRef.current?.(error),
    })
  );
  const generation = useRef(0);
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<
    Pick<UseLockerverseAuctionResult, "auction" | "loading" | "error">
  >(() => {
    const { initialAuction } = refreshOptions;
    if (initialAuction && initialAuction.slug !== auctionSlug) {
      throw new TypeError(
        "Initial auction slug must match the requested auction."
      );
    }
    return { auction: initialAuction ?? null, error: null, loading: true };
  });

  const refresh = useCallback(() => {
    generation.current += 1;
    setState((current) => ({ ...current, error: null, loading: true }));
    setRevision((current) => current + 1);
  }, []);
  const startRead = useAuctionReturnRefresh(refresh, refreshOptions);

  useEffect(() => {
    let active = true;
    const requestGeneration = generation.current;
    const settleRead = startRead();
    client
      .load(
        auctionSlug,
        refreshOptions.previewItemSlug
          ? { previewItemSlug: refreshOptions.previewItemSlug }
          : undefined
      )
      .then(
        (auction) => {
          if (active && requestGeneration === generation.current) {
            setState({ auction, error: null, loading: false });
          }
        },
        (error: LockerverseSdkError) => {
          if (active && requestGeneration === generation.current) {
            setState((current) => ({ ...current, error, loading: false }));
          }
        }
      )
      .finally(settleRead);
    return () => {
      active = false;
    };
  }, [
    client,
    auctionSlug,
    refreshOptions.previewItemSlug,
    revision,
    startRead,
  ]);

  return { ...state, client, refresh };
}
